import { Icon } from "@focus/focusUI";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ContentHeader } from "../components/ContentHeader";
import { QuestionnaireForm } from "../components/QuestionnaireForm";
import { sendQuestionnarie } from "../services/questionnaires";

type SectionId = "support" | "od" | "calibration" | "observations";
type AdditionalServicesTabId = SectionId | "contact";

interface ContactFormState {
  email: string;
  message: string;
  name: string;
  organization: string;
  service: string;
}

interface PricingCardProps {
  description?: ReactNode;
  eyebrow?: string;
  footer?: ReactNode;
  price: ReactNode;
  title: string;
}

interface ServiceSectionProps {
  children: ReactNode;
  description: ReactNode;
  icon: string;
  id: SectionId;
  title: string;
}

const contactServiceOptions = [
  { label: "24/7 CA Expert Support", value: "24/7 CA Expert Support" },
  {
    label: "Sensor Tasking and Orbit Determination",
    value: "Sensor Tasking and Orbit Determination",
  },
  { label: "Sensor Calibration", value: "Sensor Calibration" },
  { label: "External Observations", value: "External Observations" },
  { label: "Other additional service", value: "Other additional service" },
];
const initialContactForm: ContactFormState = {
  email: "",
  message: "",
  name: "",
  organization: "",
  service: contactServiceOptions[0].label,
};

const pricingSectionTabs: Array<{
  id: SectionId;
  show: boolean;
  title: string;
}> = [
  { id: "support", title: "24/7 Support", show: true },
  { id: "od", title: "Orbit Determination", show: true },
  { id: "calibration", title: "Sensor Calibration", show: true },
  // { id: "observations", title: "Reference Prices", show: true },
];

const sectionTabs: Array<{
  id: AdditionalServicesTabId;
  show: boolean;
  title: string;
}> = [
  ...pricingSectionTabs,
  { id: "contact", title: "Contact Us", show: true },
];

const supportPrices = [
  { id: 1, range: "1-10", price: "$10,000" },
  { id: 2, range: "11-50", price: "$18,000" },
  { id: 3, range: "51-200", price: "$32,500" },
  { id: 4, range: "201-500", price: "$44,000" },
  { id: 5, range: "501-1000", price: "$70,500" },
  { id: 6, range: "1001-2000", price: "$110,000" },
  { id: 7, range: ">2000", price: "$177,000" },
];

// const referencePrices = [
//   {
//     id: 1,
//     provider: "GMV",
//     type: "Passive RF (Focusear)",
//     price: "$300 / satellite",
//     note: "5 days",
//   },
//   {
//     id: 2,
//     provider: "Slingshot",
//     type: "Optical",
//     price: "660 EUR / satellite",
//     note: "5 days",
//   },
//   {
//     id: 3,
//     provider: "Spaceflux",
//     type: "Optical",
//     price: "400 EUR GEO / 550 EUR LEO",
//     note: "Per satellite, 5 days",
//   },
//   {
//     id: 4,
//     provider: "EOS",
//     type: "Optical + laser ranging",
//     price: "5,000 EUR / satellite",
//     note: "5 days",
//   },
// ];

const ContactLink = ({
  children,
  onOpenContact,
}: {
  children?: ReactNode;
  onOpenContact: () => void;
}) => (
  <button
    className="cursor-pointer border-0 bg-transparent p-0 font-bold text-sky-600 underline decoration-sky-300 underline-offset-2 transition hover:text-sky-800"
    onClick={onOpenContact}
    type="button"
  >
    {children ?? "contact us"}
  </button>
);

const scrollIntoSection = (section: HTMLElement) => {
  section.scrollIntoView({ behavior: "smooth", block: "start" });
};

const PricingCard = ({
  description,
  eyebrow,
  footer,
  price,
  title,
}: PricingCardProps) => (
  <article className="flex min-h-[190px] min-w-0 flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
    {eyebrow && (
      <div className="text-xs font-semibold uppercase text-slate-500">
        {eyebrow}
      </div>
    )}
    <h3 className="mt-1 text-base font-bold text-slate-950">{title}</h3>
    <div className="mt-3 text-2xl font-bold text-slate-950">{price}</div>
    {description && (
      <div className="mt-3 text-sm font-medium leading-6 text-slate-600">
        {description}
      </div>
    )}
    {footer && (
      <div className="mt-auto pt-4 text-xs font-semibold uppercase text-slate-500">
        {footer}
      </div>
    )}
  </article>
);

const ServiceSection = ({
  children,
  description,
  icon,
  id,
  title,
}: ServiceSectionProps) => (
  <section
    className="scroll-mt-4 rounded-lg border border-slate-200 bg-white shadow-sm"
    id={id}
  >
    <div className="border-b border-slate-200 bg-slate-50/80 px-5 py-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="flex size-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm">
            <Icon icon={icon} />
          </span>
          <h2 className="text-xl font-bold text-slate-950">{title}</h2>
        </div>
        <div className="mt-2 max-w-4xl text-sm font-medium leading-6 text-slate-600">
          {description}
        </div>
      </div>
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const PageIntro = () => (
  <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
    <div className="max-w-5xl">
      <div className="text-xs font-semibold uppercase text-slate-500">
        Service catalogue
      </div>
      <h1 className="mt-2 text-2xl font-bold text-slate-950">
        Operational add-ons for Space Safety Portal users
      </h1>
      <p className="mt-3 text-sm font-medium leading-6 text-slate-600">
        Browse support, orbit determination and calibration services in one
        place. Prices are indicative unless explicitly stated and can be adapted
        for routine or high-volume needs.
        {/* Browse support, orbit determination, calibration and external
        observation reference pricing in one place. Prices are indicative unless
        explicitly stated and can be adapted for routine or high-volume needs. */}
      </p>
    </div>
  </section>
);

const SupportSection = ({ onOpenContact }: { onOpenContact: () => void }) => (
  <ServiceSection
    id="support"
    icon="wrench"
    title="24/7 CA Expert Support"
    description={
      <>
        CA operations support is available in nominal working hours, from 09:00
        to 18:00 CET, Monday to Friday excluding Madrid local holidays. 24/7
        support, 365 days a year, is available as an additional yearly service
        per operator.
      </>
    }
  >
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {supportPrices.map((tier) => (
        <PricingCard
          eyebrow="Yearly support"
          key={tier.id}
          price={tier.price}
          title={`${tier.range} satellites`}
          description="24/7 CA expert support coverage for one operator."
          footer="Yearly price per operator"
        />
      ))}
    </div>
    <p className="mt-4 text-sm font-medium text-slate-600">
      Other options can be quoted upon request. Please{" "}
      <ContactLink onOpenContact={onOpenContact} /> for more information.
    </p>
  </ServiceSection>
);

const OrbitDeterminationSection = ({
  onOpenContact,
  onOpenReferencePrices,
}: {
  onOpenContact: () => void;
  onOpenReferencePrices: () => void;
}) => (
  <ServiceSection
    id="od"
    icon="social-media"
    title="Sensor Tasking and Orbit Determination"
    description={
      <>
        Sensor tasking and orbit determination pricing combines external
        observations and GMV-provided OD services. GMV facilitates SDA members
        choosing their preferred tracking data provider.
      </>
    }
  >
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <PricingCard
        eyebrow="External observations"
        price="Provider price"
        title="Purchase of external observations"
        description={
          <>
            Price depends on the selected tracking data provider.
            {/* <button
              className="font-semibold text-slate-950 hover:underline"
              onClick={onOpenReferencePrices}
              type="button"
            >
              See reference prices
            </button> */}
            {/* . */}
          </>
        }
        footer="No additional GMV overhead"
      />
      <PricingCard
        eyebrow="GMV service"
        price="$1,750"
        title="Orbit determination service"
        description="Per OD service and per satellite, assuming a 5-day span."
        footer="Single service price"
      />
    </div>
    <p className="mt-4 text-sm font-medium text-slate-600">
      For routine services, different pricing policies may apply. Please{" "}
      <ContactLink onOpenContact={onOpenContact} /> with your needs.
    </p>
  </ServiceSection>
);

const CalibrationSection = ({
  onOpenContact,
  onOpenReferencePrices,
}: {
  onOpenContact: () => void;
  onOpenReferencePrices: () => void;
}) => (
  <ServiceSection
    id="calibration"
    icon="antenna"
    title="Sensor Calibration"
    description={
      <>
        Sensor calibration pricing combines reference observations and a GMV
        calibration campaign. Reference observations are charged at provider
        price with no additional overhead cost by GMV.
      </>
    }
  >
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <PricingCard
        eyebrow="Reference observations"
        price="Provider price"
        title="Purchase of calibration observations"
        description={
          <>
            Price is defined by each provider.
            {/* <button
              className="font-semibold text-slate-950 hover:underline"
              onClick={onOpenReferencePrices}
              type="button"
            >
              See reference prices
            </button> */}
            {/* . */}
          </>
        }
        footer="No additional GMV overhead"
      />
      <PricingCard
        eyebrow="GMV service"
        price="$3,000"
        title="Sensor calibration campaign"
        description="For one satellite and up to 2 sensors, assuming a 5-day span."
        footer="Single campaign price"
      />
    </div>
    <p className="mt-4 text-sm font-medium text-slate-600">
      For routine services, different pricing policies may apply. Please{" "}
      <ContactLink onOpenContact={onOpenContact} /> with your needs.
    </p>
  </ServiceSection>
);

// const ReferencePricesSection = () => (
//   <ServiceSection
//     id="observations"
//     icon="satellite"
//     title="Reference Prices for External Observations"
//     description={
//       <>
//         When using 3rd party observations, GMV charges only the provider price.
//         These reference prices are estimated for 2025 and final prices may vary
//         depending on the specific case.
//       </>
//     }
//   >
//     <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
//       {referencePrices.map((item) => (
//         <PricingCard
//           eyebrow={item.type}
//           key={item.id}
//           price={item.price}
//           title={item.provider}
//           description={item.note}
//           footer="Reference price"
//         />
//       ))}
//     </div>
//   </ServiceSection>
// );

const ContactCtaSection = ({
  onOpenContact,
}: {
  onOpenContact: () => void;
}) => (
  <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase text-slate-500">
          Contact
        </div>
        <h2 className="mt-1 text-xl font-bold text-slate-950">
          Need a tailored quote?
        </h2>
        <p className="mt-2 text-sm font-medium leading-6 text-slate-600">
          Send a structured inquiry to the Space Safety Portal team from a
          dedicated contact view.
        </p>
      </div>
      <button
        className="focusoc-theme-primary inline-flex min-h-10 cursor-pointer items-center rounded-lg border px-4 text-sm font-bold shadow-sm transition"
        onClick={onOpenContact}
        type="button"
      >
        Contact us
      </button>
    </div>
  </section>
);

export const AdditionalServicesContainer = () => {
  const [selectedTabId, setSelectedTabId] =
    useState<AdditionalServicesTabId>("support");
  const mainRef = useRef<HTMLElement | null>(null);
  const pendingScrollSectionRef = useRef<SectionId | null>(null);
  const sectionRefs = useRef<Record<SectionId, HTMLElement | null>>({
    support: null,
    od: null,
    calibration: null,
    observations: null,
  });

  const tabs = useMemo(
    () =>
      sectionTabs.map((tab) => ({
        ...tab,
        render: () => null,
      })),
    [],
  );

  const scrollToSection = useCallback((id: SectionId) => {
    setSelectedTabId(id);
    const section = sectionRefs.current[id];

    if (section) {
      scrollIntoSection(section);
      return;
    }

    pendingScrollSectionRef.current = id;
  }, []);
  const openContactView = useCallback(() => {
    pendingScrollSectionRef.current = null;
    setSelectedTabId("contact");
    mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const handleSelectTab = useCallback(
    (id: AdditionalServicesTabId) => {
      if (id === "contact") {
        openContactView();
        return;
      }

      scrollToSection(id);
    },
    [openContactView, scrollToSection],
  );

  useLayoutEffect(() => {
    if (selectedTabId === "contact") return;

    const pendingSectionId = pendingScrollSectionRef.current;
    if (!pendingSectionId) return;

    const section = sectionRefs.current[pendingSectionId];
    if (!section) return;

    section.scrollIntoView({ behavior: "auto", block: "start" });
    pendingScrollSectionRef.current = null;
  }, [selectedTabId]);

  useEffect(() => {
    if (selectedTabId === "contact") return;

    const container = mainRef.current;
    if (!container) return;

    const handleScroll = () => {
      const bottomOffset =
        container.scrollHeight - container.scrollTop - container.clientHeight;

      if (bottomOffset <= 2) {
        setSelectedTabId(pricingSectionTabs[pricingSectionTabs.length - 1].id);
        return;
      }

      const containerTop = container.getBoundingClientRect().top;
      const threshold = 80;
      const activeId = pricingSectionTabs.reduce<SectionId>((current, tab) => {
        const section = sectionRefs.current[tab.id];
        if (!section) return current;

        const offsetFromTop =
          section.getBoundingClientRect().top - containerTop;

        return offsetFromTop <= threshold ? tab.id : current;
      }, pricingSectionTabs[0].id);

      setSelectedTabId(activeId);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => container.removeEventListener("scroll", handleScroll);
  }, [selectedTabId]);

  return (
    <div className="foc-content foc-additional-services flex flex-col overflow-hidden">
      <ContentHeader
        tabs={tabs}
        selectedTab={selectedTabId}
        handleSelectTab={(id) => handleSelectTab(id as AdditionalServicesTabId)}
        tabsAlign="start"
      />

      <main
        className={`min-h-0 flex-1 bg-slate-50/40 p-5 ${
          selectedTabId === "contact" ? "overflow-hidden" : "overflow-y-auto"
        }`}
        ref={mainRef}
      >
        <div
          className={`mx-auto flex w-full max-w-[1500px] flex-col ${
            selectedTabId === "contact" ? "h-full min-h-0" : "gap-8"
          }`}
        >
          {selectedTabId === "contact" ? (
            <QuestionnaireForm
              title="Need a tailored quote?"
              handleSubmit={sendQuestionnarie}
              initialData={initialContactForm}
              contactServiceOptions={contactServiceOptions}
              reason="Tell us which additional service you are interested in and what
            operational context we should consider"
            />
          ) : (
            <>
              <PageIntro />

              <div
                ref={(node) => {
                  sectionRefs.current.support = node;
                }}
              >
                <SupportSection onOpenContact={openContactView} />
              </div>

              <div
                ref={(node) => {
                  sectionRefs.current.od = node;
                }}
              >
                <OrbitDeterminationSection
                  onOpenContact={openContactView}
                  // onOpenReferencePrices={() => scrollToSection("observations")}
                />
              </div>

              <div
                ref={(node) => {
                  sectionRefs.current.calibration = node;
                }}
              >
                <CalibrationSection
                  onOpenContact={openContactView}
                  // onOpenReferencePrices={() => scrollToSection("observations")}
                />
              </div>

              {/* <div
                ref={(node) => {
                  sectionRefs.current.observations = node;
                }}
              >
                <ReferencePricesSection />
              </div> */}

              <ContactCtaSection onOpenContact={openContactView} />
            </>
          )}
        </div>
      </main>
    </div>
  );
};
